import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Course } from '../../../../../models/interfaces/course.interface';
import { Observable, of } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { cloneDeep } from 'lodash-es';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-modal-assign-course',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, EnfafModalComponent, MultiAutocompleteLightComponent, EnfafIconComponent],
  templateUrl: './modal-assign-course.component.html',
  styleUrl: './modal-assign-course.component.scss'
})
export class ModalAssignCourseComponent implements OnInit {
  studentForm: FormGroup = new FormGroup({});
  showCloseAlert = false;
  isAssigning = false;
  studentId!: string;
  fullname!: string;
  availableCourses: SelectOption[] = [];
  disabledCourses: SelectOption[] = [];
  selectedCourses: SelectOption[] = [];

  private destroyRef = inject(DestroyRef);

  get title(): string {
    return `Asignar cursos a: ${this.fullname}`;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalAssignCourseComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.studentId = receivedData?.studentId;
    this.fullname = receivedData?.fullname;
    const courses: Array<Course> = receivedData?.availableCourses;
    const obtainedCourses: Array<Course> = receivedData?.obtainedCourses;

    if (!this.studentId || !this.fullname || !courses?.length) {
      this.closeModal();
      return;
    }

    // Convert courses to SelectOption format
    this.availableCourses = courses.map((course) => ({
      id: course.courseId,
      title: course.name
    }));

    // Mark already obtained courses as disabled
    if (obtainedCourses?.length) {
      this.disabledCourses = obtainedCourses.map((course) => ({
        id: course.courseId,
        title: course.name
      }));
    }

    this.studentForm = new FormGroup({
      courses: new FormControl([], Validators.required)
    });

    this.listenForChanges();
  }

  listenForChanges(): void {
    this.studentForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showCloseAlert = true;
    });
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  fetchCourses(search: string, _page: number, _pageSize: number): Observable<{ data: SelectOption[]; total: number }> {
    const filtered = this.availableCourses.filter((course) => course.title.toLowerCase().includes(search.toLowerCase()));
    return of({ data: filtered, total: filtered.length });
  }

  onSelectedCoursesChanged(courses: SelectOption[]): void {
    this.selectedCourses = courses;
    this.studentForm.controls['courses'].setValue(courses.map((course) => course.id));
  }

  removeCourse(courseId: string): void {
    this.selectedCourses = this.selectedCourses.filter((course) => course.id !== courseId);
    this.studentForm.controls['courses'].setValue(this.selectedCourses.map((course) => course.id));
  }

  async assignCourses(): Promise<void> {
    try {
      this.isAssigning = true;
      const courseIds = this.studentForm.value.courses as string[];

      const body = {
        studentId: this.studentId,
        courses: courseIds
      };
      const response = await firstValueFrom(this.api.assignCourses(body));
      this.isAssigning = false;
      this.closeModal({ studentUpdated: response.studentUpdated });
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isAssigning = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error asignando cursos');
    }
  }
}
